import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import api from '../services/api';
import { getApiErrorMessage } from '../services/api';

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setFieldErrors({ email: 'メールアドレスを入力してください。' });
      scrollToField('email');
      return;
    }
    setFieldErrors({});

    setLoading(true);
    setError(null);

    try {
      await api.adminStore.passwordReset(email);
      setSuccess(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'パスワードリセットメールの送信に失敗しました。'));
      console.error('パスワードリセットエラー:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f8f8f9',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <img
                src="/recrew_logo_A-3.png"
                alt="Re:crew Logo"
                style={{
                  height: '40px',
                  width: 'auto',
                }}
              />
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              パスワードリセット
            </Typography>
          </Box>

          {success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                パスワードリセットメールを送信しました。メールに記載されたリンクからパスワードを再設定してください。
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  bgcolor: '#3E4298',
                  '&:hover': { bgcolor: '#4b49d6' },
                }}
              >
                ログイン画面に戻る
              </Button>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                登録済みのメールアドレスを入力してください。パスワードリセット用のメールをお送りします。
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  label="メールアドレス"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) {
                      setFieldErrors((prev) => {
                        const { email, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                  disabled={loading}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    bgcolor: '#3E4298',
                    '&:hover': { bgcolor: '#4b49d6' },
                    '&:disabled': { bgcolor: '#9c9ce6' },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'リセットメールを送信'
                  )}
                </Button>
              </form>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ color: '#3E4298' }}
                >
                  ログイン画面に戻る
                </Link>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswordReset;
